import { Helmet, HelmetProvider } from "react-helmet-async";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { addDays, isBefore } from "date-fns";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";

import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { FileLabel, FileLogStatus } from "shared/types/file";
import { useUsers } from "shared/contexts/users.context";
import {
  FileUploadField,
} from "shared/components/uploadFileModal/uploadFileModal";
import ModalUploadFileLog from "shared/components/uploadFileModal/uploadFileModal";
import { ArrowUpRight } from "assets/icons";
import { PageInfoBanner } from "@/shared/components/walkthrough";

import { DueDiligenceDocumentLogModal } from "./components/document-log-modal";
import { DueDiligenceTable } from "./components/DueDiligenceTable";
import { DueDiligenceSectionHeader } from "./components/DueDiligenceSectionHeader";
import { DueDiligenceStickyControls } from "./components/DueDiligenceStickyControls";
import { printStyles } from "./components/print-styles";
import {
  type BuildingRow,
  type DocumentLog,
  type LabelCountMap,
  type RequiredLabels,
  type ResourceRow,
  type UnitRow,
  isLabelRequired,
} from "./components/types";

interface ProcessedData {
  buildings: Record<string, Record<string, DocumentLog[]>>;
  units: Record<string, Record<string, DocumentLog[]>>;
}

interface MissingDocumentsResponse {
  buildings?: Record<string, Record<string, DocumentLog[]>>;
  units?: Record<string, Record<string, DocumentLog[]>>;
  requiredLabels?: RequiredLabels;
}

interface SelectedResource {
  id: string;
  isBuilding: boolean;
  name: string;
  label: string;
}

interface UploadModalResource {
  id: string;
  isBuilding: boolean;
}

const useScrollSection = (
  buildingTableRef: React.RefObject<HTMLDivElement>,
  unitTableRef: React.RefObject<HTMLDivElement>,
) => {
  const [currentSection, setCurrentSection] = useState<
    "buildings" | "units" | null
  >(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!buildingTableRef.current || !unitTableRef.current) return;

      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;

      const buildingTableTop =
        buildingTableRef.current.getBoundingClientRect().top + scrollY;
      const buildingTableBottom =
        buildingTableTop + buildingTableRef.current.offsetHeight;
      const unitTableTop =
        unitTableRef.current.getBoundingClientRect().top + scrollY;
      const unitTableBottom =
        unitTableTop + unitTableRef.current.offsetHeight;

      const middleOfViewport = scrollY + viewportHeight / 2;
      if (
        middleOfViewport >= unitTableTop &&
        middleOfViewport <= unitTableBottom
      ) {
        setCurrentSection("units");
      } else if (
        middleOfViewport >= buildingTableTop &&
        middleOfViewport <= buildingTableBottom
      ) {
        setCurrentSection("buildings");
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [buildingTableRef, unitTableRef]);

  return { currentSection, setCurrentSection };
};

const DueDiligence = () => {
  const { session } = useSession();
  const { users } = useUsers();

  const [refreshing, setRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [documentData, setDocumentData] = useState<ProcessedData | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBuildings, setSelectedBuildings] = useState<Set<string>>(new Set());

  const buildingTableRef = useRef<HTMLDivElement>(null);
  const unitTableRef = useRef<HTMLDivElement>(null);
  const buildingLabelsRef = useRef<HTMLDivElement>(null);
  const unitLabelsRef = useRef<HTMLDivElement>(null);

  const { currentSection, setCurrentSection } = useScrollSection(
    buildingTableRef,
    unitTableRef,
  );

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadModalResource, setUploadModalResource] = useState<UploadModalResource | null>(null);
  const [uploadModalLabel, setUploadModalLabel] = useState<string | null>(null);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState<DocumentLog[]>([]);
  const [selectedResource, setSelectedResource] = useState<SelectedResource | null>(null);

  const [availableBuildingLabels, setAvailableBuildingLabels] = useState<string[]>([]);
  const [availableUnitLabels, setAvailableUnitLabels] = useState<string[]>([]);
  const [selectedBuildingLabels, setSelectedBuildingLabels] = useState<Set<string>>(new Set());
  const [selectedUnitLabels, setSelectedUnitLabels] = useState<Set<string>>(new Set());

  const [requiredLabels, setRequiredLabels] = useState<RequiredLabels>({
    buildings: {},
    units: {},
  });

  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const [buildingLabelsExpanded, setBuildingLabelsExpanded] = useState(false);
  const [unitLabelsExpanded, setUnitLabelsExpanded] = useState(false);

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  const sortedBuildingLabels = useMemo(
    () =>
      Array.from(selectedBuildingLabels).sort((a, b) =>
        a.localeCompare(b, "nl"),
      ),
    [selectedBuildingLabels],
  );

  const sortedUnitLabels = useMemo(
    () =>
      Array.from(selectedUnitLabels).sort((a, b) =>
        a.localeCompare(b, "nl"),
      ),
    [selectedUnitLabels],
  );

  const getUserName = useCallback(
    (userId: string) => {
      const foundUser = users.find((user) => user.id === userId);
      return foundUser
        ? `${foundUser.first_name} ${foundUser.last_name}`
        : "Onbekende gebruiker";
    },
    [users],
  );

  // Collapse expanded label sections on intentional scrolling
  useEffect(() => {
    let lastScrollY = window.scrollY;
    const scrollThreshold = 50;
    let isIntentionalScroll = false;
    let scrollTimer: ReturnType<typeof setTimeout> | null = null;

    const handleScrollStart = () => {
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        isIntentionalScroll = true;
      }, 150);
    };

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDistance = Math.abs(currentScrollY - lastScrollY);

      if (scrollDistance > 5 && !isIntentionalScroll) {
        handleScrollStart();
      }

      if (scrollDistance > scrollThreshold && isIntentionalScroll) {
        if (buildingLabelsExpanded || unitLabelsExpanded) {
          setBuildingLabelsExpanded(false);
          setUnitLabelsExpanded(false);
        }
        isIntentionalScroll = false;
        if (scrollTimer) clearTimeout(scrollTimer);
      }

      lastScrollY = currentScrollY;
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 1) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("mousedown", handleMouseDown);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousedown", handleMouseDown);
      if (scrollTimer) clearTimeout(scrollTimer);
    };
  }, [buildingLabelsExpanded, unitLabelsExpanded]);

  const fetchDocumentData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/file/missing-documents/buildings`,
        { method: "GET" },
        session,
      );

      if (!res.ok) {
        const error = new Error(
          `Failed to fetch missing documents: ${res.status} ${res.statusText}`,
        );
        Sentry.captureException(error);
        toast.error("Kon documenten niet ophalen");
        return;
      }

      const data: MissingDocumentsResponse = await res.json();

      const processedData: ProcessedData = { buildings: {}, units: {} };

      if (data.buildings) {
        Object.entries(data.buildings).forEach(([buildingId, buildingLabelsMap]) => {
          processedData.buildings[buildingId] = {};
          if (buildingLabelsMap && typeof buildingLabelsMap === "object") {
            Object.entries(buildingLabelsMap).forEach(([label, logs]) => {
              processedData.buildings[buildingId][label] = logs;
            });
          }
        });
      }

      if (data.units) {
        Object.entries(data.units).forEach(([unitId, unitLabelsMap]) => {
          processedData.units[unitId] = {};
          if (unitLabelsMap && typeof unitLabelsMap === "object") {
            Object.entries(unitLabelsMap).forEach(([label, logs]) => {
              processedData.units[unitId][label] = logs;
            });
          }
        });
      }

      if (data.requiredLabels) {
        setRequiredLabels(data.requiredLabels);
      }

      setDocumentData(processedData);

      const buildingLabels = new Set<string>();
      const unitLabels = new Set<string>();

      Object.values(processedData.buildings).forEach((labelsObj) => {
        Object.keys(labelsObj).forEach((label) => buildingLabels.add(label));
      });
      Object.values(processedData.units).forEach((labelsObj) => {
        Object.keys(labelsObj).forEach((label) => unitLabels.add(label));
      });

      setAvailableBuildingLabels(Array.from(buildingLabels));
      setAvailableUnitLabels(Array.from(unitLabels));
      setSelectedBuildingLabels(buildingLabels);
      setSelectedUnitLabels(unitLabels);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "due-diligence", action: "fetch-documents" },
      });
      toast.error("Fout bij ophalen van documenten");
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchDocumentData();
  }, [fetchDocumentData]);

  const refreshDocumentCache = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/file/clear-caches`,
        { method: "POST" },
        session,
      );

      if (!res.ok) {
        const error = new Error(
          `Failed to clear cache: ${res.status} ${res.statusText}`,
        );
        Sentry.captureException(error);
        toast.error("Kon de document cache niet vernieuwen");
        return;
      }

      toast.success("Document cache vernieuwd");
      fetchDocumentData();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "due-diligence", action: "refresh-cache" },
      });
      toast.error("Fout bij vernieuwen van document cache");
    } finally {
      setRefreshing(false);
    }
  }, [session, fetchDocumentData]);

  const filteredBuildings = useMemo<BuildingRow[]>(() => {
    if (!documentData?.buildings) return [];

    const buildings: BuildingRow[] = Object.entries(documentData.buildings).map(
      ([buildingId, labelsObj]) => {
        let address = buildingId;
        if (labelsObj) {
          const firstLabel = Object.keys(labelsObj)[0];
          if (firstLabel) {
            const firstLogs = labelsObj[firstLabel];
            if (firstLogs && firstLogs.length > 0 && firstLogs[0].buildingName) {
              address = firstLogs[0].buildingName;
            }
          }
        }
        return { id: buildingId, address, labels: labelsObj || {} };
      },
    );

    const filtered = buildings.filter((building) => {
      if (typeof building.address !== "string") return false;
      const lowered = searchTerm.toLowerCase();
      return (
        searchTerm === "" ||
        building.address.toLowerCase().includes(lowered) ||
        building.id.toLowerCase().includes(lowered)
      );
    });

    return filtered.sort((a, b) => a.address.localeCompare(b.address));
  }, [documentData, searchTerm]);

  const filteredUnits = useMemo<UnitRow[]>(() => {
    if (!documentData?.units) return [];

    const units: UnitRow[] = Object.entries(documentData.units).map(
      ([unitId, labelsObj]) => {
        let referencePlus: string | null = null;
        let buildingId: string | null = null;

        if (labelsObj) {
          const firstLabel = Object.keys(labelsObj)[0];
          if (firstLabel) {
            const firstLogs = labelsObj[firstLabel];
            if (firstLogs && firstLogs.length > 0) {
              const firstLog = firstLogs[0];
              if (firstLog?.unitReferencePlus) referencePlus = firstLog.unitReferencePlus;
              if (firstLog?.buildingId) buildingId = firstLog.buildingId;
            }
          }
        }

        return { id: unitId, referencePlus, buildingId, labels: labelsObj || {} };
      },
    );

    const filtered = units.filter((unit) => {
      const lowered = searchTerm.toLowerCase();
      const matchesSearch =
        searchTerm === "" ||
        unit.id.toLowerCase().includes(lowered) ||
        (typeof unit.referencePlus === "string" &&
          unit.referencePlus.toLowerCase().includes(lowered));

      let matchesBuilding = selectedBuildings.size === 0;
      if (!matchesBuilding) {
        for (const buildingId of selectedBuildings) {
          if (unit.id.includes(buildingId)) {
            matchesBuilding = true;
            break;
          }
        }
        if (!matchesBuilding && unit.buildingId) {
          matchesBuilding = selectedBuildings.has(unit.buildingId);
        }
      }

      return matchesSearch && matchesBuilding;
    });

    return filtered.sort((a, b) => a.id.localeCompare(b.id));
  }, [documentData, searchTerm, selectedBuildings]);

  const displayedBuildings = useMemo(() => {
    if (selectedBuildings.size === 0) return filteredBuildings;
    return filteredBuildings.filter((building) =>
      selectedBuildings.has(building.id),
    );
  }, [filteredBuildings, selectedBuildings]);

  const calculateBuildingLabelCounts = useMemo<LabelCountMap>(() => {
    const counts: LabelCountMap = {};
    sortedBuildingLabels.forEach((label) => {
      counts[label] = { available: 0, total: 0 };
    });

    if (displayedBuildings.length === 0) return counts;

    displayedBuildings.forEach((building) => {
      sortedBuildingLabels.forEach((label) => {
        if (!isLabelRequired(requiredLabels.buildings, building.id, label)) return;
        counts[label].total++;
        const logs = building.labels[label] || [];
        if (logs.length !== 0) counts[label].available++;
      });
    });

    return counts;
  }, [sortedBuildingLabels, displayedBuildings, requiredLabels.buildings]);

  const calculateUnitLabelCounts = useMemo<LabelCountMap>(() => {
    const counts: LabelCountMap = {};
    sortedUnitLabels.forEach((label) => {
      counts[label] = { available: 0, total: 0 };
    });

    if (filteredUnits.length === 0) return counts;

    filteredUnits.forEach((unit) => {
      sortedUnitLabels.forEach((label) => {
        if (!isLabelRequired(requiredLabels.units, unit.id, label)) return;
        counts[label].total++;
        const logs = unit.labels[label] || [];
        if (logs.length !== 0) counts[label].available++;
      });
    });

    return counts;
  }, [sortedUnitLabels, filteredUnits, requiredLabels.units]);

  const handleCellClick = useCallback(
    (resource: ResourceRow, label: string, isBuilding: boolean) => {
      const logs = (resource.labels[label] || []) as DocumentLog[];

      if (logs.length > 0) {
        setSelectedLogs(logs);
        setSelectedResource({
          id: resource.id,
          isBuilding,
          name: resource.id,
          label,
        });
        setLogModalOpen(true);
      } else {
        setUploadModalResource({ id: resource.id, isBuilding });
        setUploadModalLabel(label);
        setUploadModalOpen(true);
      }
    },
    [],
  );

  const handleFileUploadSuccess = useCallback(() => {
    setUploadModalOpen(false);
    fetchDocumentData();
    toast.success("Document geüpload!");
  }, [fetchDocumentData]);

  const handleSelectAllBuildingLabels = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedBuildingLabels(new Set(availableBuildingLabels));
  }, [availableBuildingLabels]);

  const handleDeselectAllBuildingLabels = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedBuildingLabels(new Set());
  }, []);

  const handleSelectAllUnitLabels = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedUnitLabels(new Set(availableUnitLabels));
  }, [availableUnitLabels]);

  const handleDeselectAllUnitLabels = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedUnitLabels(new Set());
  }, []);

  const toggleBuildingLabel = useCallback(
    (label: string, event?: React.MouseEvent) => {
      event?.stopPropagation();
      setSelectedBuildingLabels((prev) => {
        const next = new Set(prev);
        if (next.has(label)) next.delete(label);
        else next.add(label);
        return next;
      });
    },
    [],
  );

  const toggleUnitLabel = useCallback(
    (label: string, event?: React.MouseEvent) => {
      event?.stopPropagation();
      setSelectedUnitLabels((prev) => {
        const next = new Set(prev);
        if (next.has(label)) next.delete(label);
        else next.add(label);
        return next;
      });
    },
    [],
  );

  const scrollToBuildings = useCallback(() => {
    if (buildingLabelsRef.current) {
      const offsetTop =
        buildingLabelsRef.current.getBoundingClientRect().top + window.scrollY;
      const headerOffset = 70;
      window.scrollTo({ top: offsetTop - headerOffset, behavior: "smooth" });
      setCurrentSection("buildings");
    }
  }, [setCurrentSection]);

  const scrollToUnits = useCallback(() => {
    if (unitLabelsRef.current) {
      const offsetTop =
        unitLabelsRef.current.getBoundingClientRect().top + window.scrollY;
      const headerOffset = 70;
      window.scrollTo({ top: offsetTop - headerOffset, behavior: "smooth" });
      setCurrentSection("units");
    }
  }, [setCurrentSection]);

  const clearAllBuildings = useCallback(() => {
    setSelectedBuildings(new Set());
    setSearchTerm("");
  }, []);

  const removeBuilding = useCallback(
    (buildingId: string) => {
      setSelectedBuildings((prev) => {
        const next = new Set(prev);
        next.delete(buildingId);
        if (next.size === 0) setSearchTerm("");
        return next;
      });
    },
    [],
  );

  const handlePrint = useCallback(() => {
    document.body.classList.add("printing-tables-only");
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        document.body.classList.remove("printing-tables-only");
      }, 500);
    }, 100);
  }, []);

  const resourceMatchesStatus = useCallback(
    (resource: ResourceRow, label: string, isBuilding: boolean) => {
      const requiredCollection = isBuilding
        ? requiredLabels.buildings
        : requiredLabels.units;

      const required = isLabelRequired(requiredCollection, resource.id, label);

      if (!required && selectedStatuses.includes("notRequired")) return true;

      const logs = (resource.labels[label] || []) as DocumentLog[];
      if (logs.length === 0) return selectedStatuses.includes("missing");

      const latestLog = logs[0];
      const expirationDate = latestLog.expirationDate
        ? new Date(latestLog.expirationDate as string)
        : null;
      const isExpired = !!expirationDate && isBefore(expirationDate, new Date());
      const isExpiringSoon =
        !!expirationDate &&
        !isExpired &&
        isBefore(expirationDate, addDays(new Date(), 30));

      if (isExpired && selectedStatuses.includes("expired")) return true;
      if (isExpiringSoon && selectedStatuses.includes("expiring")) return true;
      if (
        latestLog.status === FileLogStatus.NOT_OK &&
        selectedStatuses.includes("notOk")
      ) {
        return true;
      }
      if (
        latestLog.status === FileLogStatus.OK &&
        selectedStatuses.includes("ok")
      ) {
        return true;
      }
      return false;
    },
    [requiredLabels, selectedStatuses],
  );

  const statusFilteredBuildings = useMemo(() => {
    if (selectedStatuses.length === 0) return displayedBuildings;
    return displayedBuildings.filter((building) =>
      sortedBuildingLabels.some((label) =>
        resourceMatchesStatus(building, label, true),
      ),
    );
  }, [displayedBuildings, selectedStatuses, sortedBuildingLabels, resourceMatchesStatus]);

  const statusFilteredUnits = useMemo(() => {
    if (selectedStatuses.length === 0) return filteredUnits;
    return filteredUnits.filter((unit) =>
      sortedUnitLabels.some((label) => resourceMatchesStatus(unit, label, false)),
    );
  }, [filteredUnits, selectedStatuses, sortedUnitLabels, resourceMatchesStatus]);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Due Diligence | Yally</title>
          <style>{printStyles}</style>
        </Helmet>
      </HelmetProvider>

      <div className="w-full px-4 py-4 pb-36">
        <PageInfoBanner pageId="dd-operational" />

        <div className="mb-4 flex items-center justify-end">
          <a
            href="https://www.ilovepdf.com"
            target="_blank"
            className="border-moss-primary/30 bg-moss-primary/5 text-moss-primary hover:bg-moss-primary/10 flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors"
            rel="noopener noreferrer"
          >
            I love pdf <ArrowUpRight />
          </a>
        </div>

        <div ref={buildingTableRef} className="print-section">
          <DueDiligenceSectionHeader
            ref={buildingLabelsRef}
            title="Gebouwen"
            count={statusFilteredBuildings.length}
            total={displayedBuildings.length}
            icon="building"
          />

          <DueDiligenceTable
            ariaLabel="Building Documents Table"
            idColumnTitle="Gebouw id"
            rows={statusFilteredBuildings}
            labels={sortedBuildingLabels}
            labelCounts={calculateBuildingLabelCounts}
            isLoading={isLoading}
            isBuilding={true}
            required={requiredLabels}
            emptyText="Geen gebouwen gevonden"
            onCellClick={handleCellClick}
            renderRowId={(row) => (row as BuildingRow).address}
          />
        </div>

        <div ref={unitTableRef} id="unitTableRef" className="print-section mt-6">
          <DueDiligenceSectionHeader
            ref={unitLabelsRef}
            title="Units"
            count={statusFilteredUnits.length}
            total={filteredUnits.length}
            icon="unit"
          />

          <DueDiligenceTable
            ariaLabel="Unit Documents Table"
            idColumnTitle="Unit id"
            rows={statusFilteredUnits}
            labels={sortedUnitLabels}
            labelCounts={calculateUnitLabelCounts}
            isLoading={isLoading}
            isBuilding={false}
            required={requiredLabels}
            emptyText="Geen units gevonden"
            onCellClick={handleCellClick}
            renderRowId={(row) => {
              const unit = row as UnitRow;
              return (
                <>
                  {unit.id} {unit.referencePlus && `(${unit.referencePlus})`}
                </>
              );
            }}
          />
        </div>
      </div>

      <div className="no-print">
        <DueDiligenceStickyControls
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isSearchFocused={isSearchFocused}
          setIsSearchFocused={setIsSearchFocused}
          filteredBuildings={filteredBuildings}
          selectedBuildings={selectedBuildings}
          setSelectedBuildings={setSelectedBuildings}
          onClearAllBuildings={clearAllBuildings}
          onRemoveBuilding={removeBuilding}
          selectedStatuses={selectedStatuses}
          setSelectedStatuses={setSelectedStatuses}
          currentSection={currentSection}
          scrollToBuildings={scrollToBuildings}
          scrollToUnits={scrollToUnits}
          availableBuildingLabels={availableBuildingLabels}
          availableUnitLabels={availableUnitLabels}
          selectedBuildingLabels={selectedBuildingLabels}
          selectedUnitLabels={selectedUnitLabels}
          toggleBuildingLabel={toggleBuildingLabel}
          toggleUnitLabel={toggleUnitLabel}
          selectAllBuildingLabels={handleSelectAllBuildingLabels}
          deselectAllBuildingLabels={handleDeselectAllBuildingLabels}
          selectAllUnitLabels={handleSelectAllUnitLabels}
          deselectAllUnitLabels={handleDeselectAllUnitLabels}
          buildingLabelsExpanded={buildingLabelsExpanded}
          setBuildingLabelsExpanded={setBuildingLabelsExpanded}
          unitLabelsExpanded={unitLabelsExpanded}
          setUnitLabelsExpanded={setUnitLabelsExpanded}
          buildingLabelCounts={calculateBuildingLabelCounts}
          unitLabelCounts={calculateUnitLabelCounts}
          refreshing={refreshing}
          onRefresh={refreshDocumentCache}
          onPrint={handlePrint}
        />
      </div>

      <div className="no-print">
        {uploadModalOpen && uploadModalResource && (
          <ModalUploadFileLog
            isOpen={uploadModalOpen}
            onClose={() => setUploadModalOpen(false)}
            onFileUploadSuccess={handleFileUploadSuccess}
            isDismissable={true}
            building={
              uploadModalResource.isBuilding
                ? { id: uploadModalResource.id }
                : null
            }
            unit={
              !uploadModalResource.isBuilding
                ? { id: uploadModalResource.id }
                : null
            }
            skipFields={
              uploadModalResource.isBuilding
                ? [FileUploadField.UNIT]
                : [FileUploadField.BUILDING]
            }
            defaultFileLabel={(uploadModalLabel ?? undefined) as FileLabel | undefined}
          />
        )}

        <DueDiligenceDocumentLogModal
          isOpen={logModalOpen}
          onClose={() => setLogModalOpen(false)}
          logs={selectedLogs}
          resource={selectedResource}
          getUserName={getUserName}
        />
      </div>
    </>
  );
};

export default DueDiligence;
